
// Centralized permission utilities to standardize resource/action handling
// - Derives resource IDs from route/nav paths
// - Normalizes action names (e.g., view -> read)
// - Provides helpers for defaults without changing UI code per page

export const normalizeAction = (action: string): string => {
  const a = action.toLowerCase();
  switch (a) {
    case 'view':
    case 'index':
      return 'read';
    default:
      return a;
  }
};

// Convert a route path to a resource id used by RBAC
// Examples:
//   /app/leads -> leads
//   /app/leads/analytics -> leads_analytics
//   /leads/contacts/new -> leads_contacts_new
//   /app/leads/:id -> leads
export const deriveResourceIdFromPath = (path: string): string => {
  if (!path) return '';
  // Remove query/hash
  const clean = path.split('?')[0].split('#')[0];
  const rawSegments = clean
    .split('/')
    .filter(Boolean)
    // drop common app prefix
    .filter(seg => seg !== 'app')
    // drop dynamic params like :id
    .filter(seg => !seg.startsWith(':'));

  if (rawSegments.length === 0) return '';

  // Normalize hyphens to underscores for resource ids (e.g., system-log -> system_log)
  const normalizedSegments = rawSegments.map(seg => seg.replace(/-/g, '_'));

  // If path looks like /leads/:id or /app/leads/:id, use just the base resource
  if (normalizedSegments.length >= 2 && rawSegments[1]?.startsWith(':')) {
    return normalizedSegments[0];
  }

  return normalizedSegments.join('_');
};

// Build the default read permission from a path
export const defaultReadPermissionFromPath = (path: string): string | null => {
  const resource = deriveResourceIdFromPath(path);
  if (!resource) return null;
  return `${resource}.read`;
};
